/**
 * Gas Engineering Calculation Engine
 *
 * Standard-based utilities for Gas Safe registered engineers:
 * - Gas rate computation (metric m³/h & imperial ft³/h) from timed dial readings
 * - Gross / Net heat input (kW) and Btu/h conversion
 * - Calorific value adjustment (Btu/ft³ - Gross & Net)
 * - Flue gas analysis (CO, CO₂, O₂, CO/CO₂ ratio, flue temp, efficiency) per BS 7967
 * - Working pressure drop validation (GSIUR / GIUSP guidance)
 *
 * Reference constants per BS 7967 & standard gas engineering practice.
 * Siemens `factor` enables normal-conditions metering to m³/h conversion.
 */

/**
 * Standard Calorific Values (CV) for UK Natural Gas
 * Typically supplied at 37.5–39.9 MJ/m³ (gross) and ~34.0 MJ/m³ (net).
 * These are configurable defaults – live CVs come from the meter/region.
 */
export const CALORIFIC_VALUES = {
  naturalGasNet: 38.2,     // MJ/m³ (typical lower heat value)
  naturalGasGross: 42.3,   // MJ/m³ (typical higher heat value)
  propaneGasNet: 93.3,     // MJ/m³ (LPG - propane vapor)
  butaneGasNet: 122.0      // MJ/m³ (LPG - butane vapor)
};

/**
 * Conversion factors
 */
export const MJ_TO_KWH = 0.27778;        // 1 MJ = 0.27778 kWh
export const BTU_PER_KWH = 3412.14;     // 1 kWh = 3412.14 Btu
export const MJ_TO_BTU = 947.8;         // 1 MJ = 947.8 Btu
export const SECONDS_PER_MINUTE = 60;
export const STANDARD_CUBIC_FEET_PER_CUBIC_METER = 35.3147;

/**
 * Calculate gas rate from a timed meter reading.
 *
 * @param {Object} opts
 * @param {number} opts.meterReading - Volume passed during timed test
 * @param {('metric'|'imperial')} opts.unit - Meter units (metric m³ or imperial ft³)
 * @param {number} [opts.seconds=120] - Duration of test in seconds (default 2-minute reading)
 * @returns {Object} { m3PerHour, ft3PerHour, testSeconds }
 */
export const calculateGasRate = ({
  meterReading = 0,
  unit = 'metric',
  seconds = 120
}) => {
  const reading = Number(meterReading) || 0;
  const durationSec = Number(seconds) || 120;
  const hours = durationSec / 3600;

  if (reading <= 0 || durationSec <= 0) {
    return { m3PerHour: 0, ft3PerHour: 0, testSeconds: durationSec };
  }

  if (unit === 'imperial') {
    const ft3PerHour = Number((reading / hours).toFixed(3));
    const m3PerHour = Number((ft3PerHour / STANDARD_CUBIC_FEET_PER_CUBIC_METER).toFixed(3));
    return { m3PerHour, ft3PerHour, testSeconds: durationSec };
  }

  const m3PerHour = Number((reading / hours).toFixed(3));
  const ft3PerHour = Number((m3PerHour * STANDARD_CUBIC_FEET_PER_CUBIC_METER).toFixed(3));
  return { m3PerHour, ft3PerHour, testSeconds: durationSec };
};

/**
 * Calculate heat input (kW & Btu/h) from a gas rate.
 *
 * @param {Object} opts
 * @param {number} opts.m3PerHour - Gas rate in m³/h
 * @param {number} [opts.calorificValue=38.2] - Calorific value in MJ/m³ (net)
 * @param {number} [opts.correctionFactor=1.0] - Meter correction factor / FTADJ
 * @returns {Object} { netKw, grossKw, netBtuH, grossBtuH }
 */
export const calculateHeatInput = ({
  m3PerHour = 0,
  calorificValue = CALORIFIC_VALUES.naturalGasNet,
  correctionFactor = 1.0
}) => {
  const rate = Number(m3PerHour) || 0;
  const cv = Number(calorificValue) || CALORIFIC_VALUES.naturalGasNet;
  const factor = Number(correctionFactor) || 1.0;

  if (rate <= 0) {
    return { netKw: 0, grossKw: 0, netBtuH: 0, grossBtuH: 0 };
  }

  const netKw = Number((rate * cv * factor * MJ_TO_KWH).toFixed(2));
  const grossKw = Number((netKw * 1.11).toFixed(2));
  const netBtuH = Number((netKw * BTU_PER_KWH).toFixed(0));
  const grossBtuH = Number((grossKw * BTU_PER_KWH).toFixed(0));

  return { netKw, grossKw, netBtuH, grossBtuH };
};

/**
 * Convert heat input (kW) to Btu/h.
 * @param {number} kW - Heat input in kW
 * @returns {number} Btu/h
 */
export const kWToBtuH = (kW = 0) => {
  const kw = Number(kW) || 0;
  return Number((kw * BTU_PER_KWH).toFixed(0));
};

/**
 * Convert Btu/h to kW.
 * @param {number} btuPerHour - Heat input in Btu/h
 * @returns {number} kW
 */
export const btuToKw = (btuPerHour = 0) => {
  const btu = Number(btuPerHour) || 0;
  return Number((btu / BTU_PER_KWH).toFixed(2));
};

/**
 * Flue Gas Analysis validation per BS 7967
 *
 * Key ratios:
 * - CO/CO₂ ratio < 0.004 for Gas Safe / BS 7967 compliance
 * - Some appliances tolerate up to 0.008 (older/non-room-sealed)
 *
 * @param {Object} analysis
 * @param {number} analysis.coPpm - Carbon monoxide in ppm
 * @param {number} analysis.co2Pct - Carbon dioxide as % of flue gas
 * @param {number} [analysis.o2Pct] - Excess oxygen in %
 * @param {number} [analysis.flueTempC] - Flue gas temperature °C
 * @param {number} [analysis.ambientTempC=20] - Ambient air temperature °C
 * @returns {Object} { co2Ratio, limit, compliant, excessAir, efficiency, issues }
 */
export const analyzeFlueGas = ({
  coPpm = 0,
  co2Pct = 0,
  o2Pct = 0,
  flueTempC = 0,
  ambientTempC = 20
}) => {
  const co = Number(coPpm) || 0;
  const co2 = Number(co2Pct) || 0;
  const o2 = Number(o2Pct) || 0;
  const flueTemp = Number(flueTempC) || 0;

  const issues = [];

  // CO/CO₂ ratio (BS 7967 default max 0.004, i.e. 0.4%)
  // ratio = (CO ppm / 10000) / (CO₂ % / 100)  →  unitless
  const co2Ratio = co2 > 0 ? Number((co / (co2 * 10000)).toFixed(6)) : 0;

  // Excess air % based on O₂ reading (O₂ × 100 / (21 − O₂))
  const excessAir = o2 > 0 ? Number(((o2 * 100) / (21 - o2)).toFixed(1)) : 0;

  // Compliance default: CO/CO₂ ratio < 0.004 (i.e. 0.4%)
  const compliantRatio = co2Ratio < 0.004;

  if (co2Ratio >= 0.004) {
    issues.push(`CO/CO₂ ratio of ${co2Ratio.toFixed(4)} exceeds the safety limit of 0.004 (0.4%) for room-sealed appliances. Appliance is At Risk.`);
  }

  if (flueTemp > 260) {
    issues.push(`Flue gas temperature of ${flueTemp}°C exceeds 260°C ceiling for condensing appliance – check appliance condition.`);
  }

  if (o2 > 21) {
    issues.push(`O₂ reading of ${o2}% exceeds atmospheric 21% – check probe placement in flue stream.`);
  }

  return {
    coPpm: co,
    co2Pct: co2,
    o2Pct: o2,
    flueTempC: flueTemp,
    co2Ratio,
    excessAir,
    compliant: compliantRatio && issues.length === 0,
    recommended: compliantRatio
      ? 'Combustion analysis within acceptable limits.'
      : 'Appliance producing elevated CO. Recommend service/replacement and add to warning notice.',
    issues
  };
};

/**
 * Working pressure drop validation (domestic supply)
 * GSI guidance: pressure drop between meter and appliance should not exceed 1.0 mbar.
 *
 * @param {number} meterPressureMbar - Pressure at meter outlet (mbar)
 * @param {number} appliancePressureMbar - Pressure at appliance inlet (mbar)
 * @param {number} [maxDropMbar=1.0] - Permissible drop
 * @returns {Object} { dropMbar, maxDropMbar, acceptable, issues }
 */
export const validateWorkingPressureDrop = (
  meterPressureMbar = 21,
  appliancePressureMbar = 20,
  maxDropMbar = 1.0
) => {
  const meter = Number(meterPressureMbar) || 0;
  const appliance = Number(appliancePressureMbar) || 0;
  const drop = Number((meter - appliance).toFixed(2));

  const issues = [];
  if (drop > maxDropMbar) {
    issues.push(`Working pressure drop of ${drop} mbar exceeds the maximum permissible ${maxDropMbar} mbar.`);
  }
  if (appliance <= 0) {
    issues.push('Appliance operating pressure not recorded – enter meter and appliance pressures to calculate drop.');
  }

  return {
    dropMbar: drop,
    meterPressureMbar: meter,
    appliancePressureMbar: appliance,
    maxDropMbar,
    acceptable: drop <= maxDropMbar,
    issues
  };
};

export default {
  calculateGasRate,
  kWToBtuH,
  btuToKw,
  calculateHeatInput
};